import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { CheckIcon, CloudArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import { Badge, Button, Input, Label, Spinner } from '../../../../components/ui'
import { getPdfHighlightContext, getPdfSearchText, type GmailSowAttachment } from '../../domain/contractWorkflow'
import { getErrorMessage, getGmailScanErrorMessage } from '../../domain/formatters'
import type { ContractExtractionCitation, GmailSowIntakeResponse } from '../../types'
import { formatCitationField } from '../sow/ExtractionCitations'

const PdfPageViewer = React.lazy(() => import('../../../../components/shared/PdfPageViewer'))

export function GmailIntakePanel({
  result,
  isPending,
  error,
  isExtracting,
  parsedAttachment,
  onExtractAttachment,
  onResetExtraction,
}: {
  result?: GmailSowIntakeResponse
  isPending: boolean
  error: unknown
  isExtracting: boolean
  parsedAttachment?: GmailSowAttachment
  onExtractAttachment: (attachment: GmailSowIntakeResponse['messages'][number]['downloaded_attachments'][number]) => void
  onResetExtraction: () => void
}) {
  const candidates = useMemo(() => (
    (result?.messages || [])
      .filter(message => message.matched)
      .flatMap(message => message.downloaded_attachments
        .filter(attachment => attachment.filename.toLowerCase().endsWith('.pdf'))
        .map(attachment => ({ message, attachment })))
  ), [result])
  const [selectedPath, setSelectedPath] = useState('')

  useEffect(() => {
    const selectedStillExists = candidates.some(candidate => candidate.attachment.local_path === selectedPath)
    if (!selectedStillExists) {
      setSelectedPath(candidates[0]?.attachment.local_path || '')
    }
  }, [candidates, selectedPath])

  if (isPending) {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
        <div className="flex items-center gap-3">
          <Spinner size="sm" />
          <div>
            <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">Scanning Gmail for SOW emails</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Checking message content and PDF filenames for SOW or Statement of Work.
            </p>
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5" />
          <div>
            <h2 className="font-semibold">Check your Google Workspace connections</h2>
            <p className="mt-1 text-sm">{getGmailScanErrorMessage()}</p>
          </div>
        </div>
      </section>
    )
  }

  if (!result) return null
  const selectedCandidate = candidates.find(candidate => candidate.attachment.local_path === selectedPath)
  const activeParsedCandidate = parsedAttachment
    ? candidates.find(candidate => candidate.attachment.local_path === parsedAttachment.local_path)
    : undefined

  const formatFileSize = (sizeBytes: number) => {
    if (sizeBytes < 1024) return `${sizeBytes} bytes`
    return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`
  }

  return (
    <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-card dark:border-dark-border/30 dark:bg-dark-surface">
      <div className="flex flex-wrap items-start justify-between gap-3 px-6 pb-2 pt-6">
        <div>
          <h2 className="font-subtitle text-xl text-charcoal dark:text-gray-100">Gmail SOW intake scan</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {parsedAttachment
              ? 'One SOW is active. Review its extracted fields below or choose another SOW.'
              : `Found ${result.matched_count} SOW candidate${result.matched_count === 1 ? '' : 's'}. Choose one PDF to parse.`}
            {result.account_email ? ` Connected mailbox: ${result.account_email}.` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={result.matched_count > 0 ? 'success' : 'secondary'} className="rounded-md">
            {result.matched_count} matched
          </Badge>
          <Badge variant={result.downloaded_count > 0 ? 'info' : 'secondary'} className="rounded-md">
            {result.downloaded_count} downloaded
          </Badge>
        </div>
      </div>

      {parsedAttachment ? (
        <div className="px-6 py-5">
          <div className="flex flex-col gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 sm:flex-row sm:items-center sm:justify-between dark:border-emerald-900 dark:bg-emerald-950/30">
            <div className="flex min-w-0 items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-white">
                <CheckIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Active SOW</p>
                <p className="mt-1 break-words text-sm font-semibold text-charcoal dark:text-gray-100">{parsedAttachment.filename}</p>
                {activeParsedCandidate && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    From {activeParsedCandidate.message.from || 'unknown sender'} · {activeParsedCandidate.message.date || 'No date'} · {formatFileSize(parsedAttachment.size_bytes)}
                  </p>
                )}
              </div>
            </div>
            <Badge variant="success" className="shrink-0 rounded-md">Extracted</Badge>
          </div>
        </div>
      ) : (
        <div className="space-y-2 px-6 py-4">
          {candidates.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 p-5 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
              No SOW PDF matches found in this scan window.
              {result.queries_tried?.length ? (
                <span className="mt-2 block text-xs">
                  Tried: {result.queries_tried.join(' | ')}
                </span>
              ) : null}
            </div>
          )}
          {candidates.map(({ message, attachment }) => {
            const selected = attachment.local_path === selectedPath
            const parsingThisCandidate = isExtracting && selected
            return (
              <button
                key={`${message.message_id}-${attachment.local_path}`}
                type="button"
                aria-pressed={selected}
                disabled={isExtracting}
                onClick={() => setSelectedPath(attachment.local_path)}
                className={`grid w-full min-w-0 gap-3 rounded-lg border p-4 text-left transition-colors sm:grid-cols-[auto_minmax(0,1fr)_auto] ${
                  selected
                    ? 'border-eliza-red bg-eliza-red/5 ring-1 ring-eliza-red/20 dark:bg-eliza-red/10'
                    : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:hover:bg-dark-surface-2'
                }`}
              >
                <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 ${selected ? 'border-eliza-red' : 'border-gray-300 dark:border-gray-600'}`}>
                  {selected && <span className="h-2.5 w-2.5 rounded-full bg-eliza-red" />}
                </span>
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="break-words text-sm font-semibold text-charcoal dark:text-gray-100">{attachment.filename}</span>
                    <Badge variant="success" className="rounded-md">SOW match</Badge>
                  </span>
                  <span className="mt-1 block truncate text-xs text-gray-500 dark:text-gray-400">
                    From {message.from || 'unknown sender'} · {message.date || 'No date'} · {formatFileSize(attachment.size_bytes)}
                  </span>
                  {parsingThisCandidate && (
                    <span className="mt-3 flex items-center gap-2 text-xs font-medium text-eliza-red">
                      <Spinner size="sm" /> Extracting fields and citations…
                    </span>
                  )}
                </span>
                <span className="min-w-0 text-left sm:text-right">
                  <span className="block text-[10px] font-medium uppercase text-gray-400">Message ID</span>
                  <span className="mt-1 block max-w-[160px] truncate font-mono text-xs text-gray-500 dark:text-gray-400">{message.message_id}</span>
                </span>
              </button>
            )
          })}
        </div>
      )}

      {parsedAttachment ? (
        <div className="flex flex-col gap-3 border-t border-emerald-200 bg-emerald-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-emerald-900 dark:bg-emerald-950/30">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-white"><CheckIcon className="h-4 w-4" /></span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Parsed — review the extracted fields below.</p>
              <p className="truncate text-xs text-emerald-700 dark:text-emerald-300">{parsedAttachment.filename}</p>
            </div>
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={onResetExtraction}>Choose another SOW</Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-dark-border dark:bg-dark-bg">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase text-gray-400">Selected document</p>
            <p className="mt-1 truncate text-sm font-medium text-charcoal dark:text-gray-100">
              {selectedCandidate?.attachment.filename || 'Select a SOW PDF above'}
            </p>
          </div>
          <Button
            type="button"
            variant="brand"
            disabled={!selectedCandidate || isExtracting}
            onClick={() => selectedCandidate && onExtractAttachment(selectedCandidate.attachment)}
          >
            {isExtracting && <Spinner size="sm" />}
            {isExtracting ? 'Parsing selected SOW' : 'Parse selected SOW'}
          </Button>
        </div>
      )}
    </section>
  )
}

export function ManualSowUploadPanel({
  file,
  isPending,
  error,
  onFileChange,
  onUpload,
}: {
  file?: File
  isPending: boolean
  error: unknown
  onFileChange: (file?: File) => void
  onUpload: () => void
}) {
  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card dark:border-dark-border/30 dark:bg-dark-surface">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,560px)] lg:items-end">
        {[
          <div key="manual-sow-copy" className="max-w-2xl">
            <div className="flex items-center gap-2">
              <CloudArrowUpIcon className="h-5 w-5 text-gray-400" />
              <h2 className="font-subtitle text-lg text-charcoal dark:text-gray-100">Manual SOW PDF upload</h2>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Use this fallback when Gmail intake misses a signed Statement of Work. Uploaded PDFs use the same parser, citations, approval fields, and sync flow.
            </p>
          </div>,
          <div key="manual-sow-control" className="flex w-full flex-col gap-2">
            {[
              <Label key="label" htmlFor="manual-sow-upload" className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                Signed SOW PDF
              </Label>,
              <div key="input-row" className="flex flex-col gap-2 sm:flex-row">
                {[
                  <Input
                    key="input"
                    id="manual-sow-upload"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={event => onFileChange(event.target.files?.[0])}
                  />,
                  <Button
                    key="button"
                    type="button"
                    variant="brand"
                    onClick={onUpload}
                    disabled={!file || isPending}
                  >
                    <span className="inline-flex items-center gap-2">
                      {isPending ? <Spinner size="sm" /> : <CloudArrowUpIcon className="h-4 w-4" />}
                      Upload & Parse
                    </span>
                  </Button>,
                ]}
              </div>,
              file ? (
                <p key="selected-file" className="truncate text-xs text-gray-500 dark:text-gray-400">
                  Selected: {file.name}
                </p>
              ) : null,
              error ? (
                <p key="error" className="text-sm text-rose-600 dark:text-rose-300">{getErrorMessage(error)}</p>
              ) : null,
            ]}
          </div>,
        ]}
      </div>
    </section>
  )
}

export function GmailSowPdfPreviewPanel({
  attachment,
  previewBlob,
  isLoading,
  error,
  activeCitation,
}: {
  attachment?: GmailSowAttachment
  previewBlob?: Blob
  isLoading: boolean
  error: unknown
  activeCitation?: ContractExtractionCitation
}) {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>()

  useEffect(() => {
    if (!previewBlob) {
      setPreviewUrl(undefined)
      return undefined
    }
    const nextUrl = URL.createObjectURL(previewBlob)
    setPreviewUrl(nextUrl)
    return () => URL.revokeObjectURL(nextUrl)
  }, [previewBlob])

  if (!attachment) return null

  const pdfSearchText = getPdfSearchText(activeCitation)
  const pdfHighlightContext = getPdfHighlightContext(activeCitation)
  const pageNumber = activeCitation?.page || 1

  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-card dark:border-dark-border/30 dark:bg-dark-surface">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">Extracted SOW PDF</h2>
          <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">
            {attachment.filename}
          </p>
        </div>
        <Badge variant="success" className="rounded-md">Extracted</Badge>
      </div>

      {activeCitation && (
        <div className="mt-4 rounded-lg border border-eliza-red/30 bg-eliza-red/5 p-3 dark:border-eliza-red/40 dark:bg-eliza-red/10">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="default" className="rounded-md">{formatCitationField(activeCitation.field)}</Badge>
            <span className="text-xs font-medium text-eliza-red">
              {activeCitation.page ? `Jumped to page ${activeCitation.page}` : 'Citation selected'}
              {pdfSearchText ? ' and highlighted source text' : ''}
            </span>
          </div>
          <p className="mt-2 text-xs leading-5 text-charcoal dark:text-gray-100">{activeCitation.snippet}</p>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-bg">
        {isLoading ? (
          <div className="flex min-h-[360px] items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <Spinner size="sm" />
            Loading PDF preview...
          </div>
        ) : error ? (
          <div className="flex min-h-[180px] items-start gap-3 p-5 text-sm text-rose-700 dark:text-rose-300">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-medium">PDF preview failed</p>
              <p className="mt-1">{getErrorMessage(error)}</p>
            </div>
          </div>
        ) : previewUrl ? (
          <Suspense
            fallback={
              <div className="flex min-h-[360px] items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                <Spinner size="sm" />
                Loading PDF highlighter...
              </div>
            }
          >
            <PdfPageViewer
              pdfUrl={previewUrl}
              pageNumber={pageNumber}
              highlightText={pdfSearchText}
              highlightContext={pdfHighlightContext}
              highlightRequestKey={activeCitation}
              renderAllPages
              className="h-[680px] w-full"
            />
          </Suspense>
        ) : (
          <div className="flex min-h-[180px] items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            PDF preview will appear after extraction.
          </div>
        )}
      </div>
    </section>
  )
}
